import Browser from '@wetransfer/concorde-browser';

import { ActionHelper } from '../../core-bundle/store/actions-generator';
import {
  download,
  DOWNLOAD_INTENT_ENTIRE_TRANSFER,
  DOWNLOAD_INTENT_SINGLE_FILE,
} from '../services/downloader/download';
import { TRANSFER_STATES } from '../constant/transfer-states';
import { fetchTransfer } from '../services/api/transfer';
import { normalizeTransfer } from '../helpers/normalize-transfer';
import TransferItem from '../models/transfer-item';

import Analytics from '@module/analytics';
import { actions as TransferActionTypes } from '@app/store/action/transfer';
import { actions as TransfersActionTypes } from '@app/store/action/transfers';
import NotificationActions, {
  actions as NotificationActionTypes,
} from '@app/store/action/notification';
import PendingActions from '@app/store/action/pending';
import ErrorsActions from '@app/store/action/errors';
import Helper from '@app/helper';
import TransferErrorMessages from '@app/helper/error/messages';

export const TransferDownloadActions = ActionHelper.generateActions({
  // The following actions (`downloadTransferFromPage` and `downloadTransferFileFromPage`) are used to
  // download a transfer from a page in the application. They dispatch different actions than downloading
  // from the transfer window because both can happen at the same time.
  downloadTransferFromPage,
  downloadTransferFileFromPage,

  // The following actions (`downloadTransfer` and `downloadTransferFile`) are used to download a transfer
  // from the transfer window.
  downloadTransfer,
  downloadTransferFile,
});

function downloadTransferFromPage({
  transferId,
  secret,
  recipientId,
  domainUserId,
  uploadInProgress,
}) {
  const pendingId = `downloading_${transferId}`;

  return async (dispatch) => {
    dispatch(PendingActions.setPending(pendingId));

    try {
      await download({
        transfer: { id: transferId, state: TRANSFER_STATES.DOWNLOADABLE },
        secret,
        recipientId,
        domainUserId,
        uploadInProgress,
        intent: DOWNLOAD_INTENT_ENTIRE_TRANSFER,
      });

      // Always increment the download counter when a transfer is downloaded (So also single file)
      dispatch({ type: TransfersActionTypes.DOWNLOAD_TRANSFER_SUCCESS });
    } catch {
      dispatch(
        ErrorsActions.setError('transfer', {
          downloadFailed: true,
        })
      );
      dispatch(
        NotificationActions.setNotification('error', 'something_went_wrong')
      );
    }

    dispatch(PendingActions.resetPending(pendingId));
  };
}

function downloadTransferFileFromPage({
  transferId,
  secret,
  recipientId,
  items,
  fileId,
  domainUserId,
  uploadInProgress,
}) {
  const pendingId = `downloading_${transferId}_${fileId}`;

  return async (dispatch) => {
    dispatch(PendingActions.setPending(pendingId));

    try {
      await download({
        transfer: {
          id: transferId,
          items,
          state: TRANSFER_STATES.DOWNLOADABLE,
        },
        secret,
        recipientId,
        fileIds: [fileId],
        domainUserId,
        uploadInProgress,
        intent: DOWNLOAD_INTENT_SINGLE_FILE,
      });

      // Always increment the download counter when a transfer is downloaded (So also single file)
      dispatch({ type: TransfersActionTypes.DOWNLOAD_TRANSFER_SUCCESS });
    } catch {
      dispatch(
        ErrorsActions.setError('transfer', {
          downloadFailed: true,
        })
      );
      dispatch(
        NotificationActions.setNotification('error', 'something_went_wrong')
      );
    }

    dispatch(PendingActions.resetPending(pendingId));
  };
}

function downloadTransfer({
  transfer,
  secret,
  recipientId = false,
  domainUserId,
  currentSize,
}) {
  return async (dispatch) => {
    // Inform interface that we're starting the download
    dispatch({ type: TransferActionTypes.TRANSFER_STATE_DOWNLOAD_STARTING });

    try {
      await download({
        transfer,
        secret,
        recipientId,
        domainUserId,
        intent: DOWNLOAD_INTENT_ENTIRE_TRANSFER,
      });

      const transferAnalyticsData = makeTransferAnalyticsData(
        transfer,
        'downloadAll'
      );

      pushDownloadAnalytics({
        transferAnalyticsData,
        category: 'Transfer',
        action: 'Download Start (All files)',
        value: currentSize,
      });

      dispatch({
        type: TransferActionTypes.TRANSFER_STATE_DOWNLOADING,
        transferId: transfer.id,
      });
    } catch (error) {
      dispatch({
        type: NotificationActionTypes.NOTIFICATION_TRANSFER_SET,
        text: error.message,
      });

      if (error === TransferErrorMessages.TOO_MANY_REQUESTS) {
        dispatch({ type: TransferActionTypes.TRANSFER_STATE_DOWNLOADABLE });
      } else {
        dispatch({ type: TransferActionTypes.TRANSFER_DOWNLOAD_INVALID });
      }

      return dispatch({ type: TransferActionTypes.DOWNLOAD_ERROR });
    }

    dispatch({ type: TransferActionTypes.TRANSFER_STATE_FINISHED });
  };
}

function downloadTransferFile({
  transfer,
  secret,
  recipientId = false,
  fileId,
  domainUserId,
}) {
  return async (dispatch) => {
    dispatch({
      type: TransferActionTypes.TRANSFER_STATE_DOWNLOAD_STARTING_FILE,
      file_id: fileId,
    });

    try {
      await download({
        transfer,
        secret,
        recipientId,
        fileIds: [fileId],
        domainUserId,
        intent: DOWNLOAD_INTENT_SINGLE_FILE,
      });

      const itemIndex = transfer.items.findIndex(({ id }) => id === fileId);
      const transferAnalyticsData = makeTransferAnalyticsData(
        transfer,
        'downloadFile',
        itemIndex
      );

      pushDownloadAnalytics({
        transferAnalyticsData,
        category: 'Transfer',
        action: 'Download Start (Single file)',
        label: `File ${fileId}`,
        value: transfer.items[itemIndex].size,
      });

      dispatch({
        type: TransferActionTypes.TRANSFER_STATE_DOWNLOADING_FILE,
        file_id: fileId,
      });
    } catch (error) {
      dispatch({
        type: NotificationActionTypes.NOTIFICATION_TRANSFER_SET,
        text: error,
      });

      if (error === TransferErrorMessages.TOO_MANY_REQUESTS) {
        dispatch({
          type: TransferActionTypes.TRANSFER_STATE_FINISHED_FILE,
          file_id: fileId,
        });
        dispatch({ type: TransferActionTypes.TRANSFER_STATE_DOWNLOADABLE });
      } else {
        dispatch({ type: TransferActionTypes.TRANSFER_DOWNLOAD_INVALID });
      }

      return dispatch({ type: TransferActionTypes.DOWNLOAD_ERROR });
    }

    dispatch({
      type: TransferActionTypes.TRANSFER_STATE_FINISHED_FILE,
      file_id: fileId,
    });
  };
}

function pushDownloadAnalytics({
  transferAnalyticsData,
  category,
  action,
  label,
  value,
}) {
  // Is it the first time a user is downloading
  if (Helper.getWTDownloaded() === 0) {
    // then record the days since first download
    transferAnalyticsData.days_since_first_download = Helper.getDaysSinceFirstVisit();
  }

  Helper.incrementWTDownloaded();

  Analytics.trackGoogleEvent({
    category,
    action,
    label,
    value,
    transfer: transferAnalyticsData,
    user: {
      download_count: Helper.getWTDownloaded(),
      first_visit_date: Helper.getFirstVisitDate(),
    },
  });
}

function makeTransferAnalyticsData(transfer, mode, fileIndex) {
  const eventData = {
    mode,
    type: transfer.type,
    item_count: transfer.items.length,
    app_type: Browser.isMobile ? 'mobile' : 'web',
    receiver_count: transfer.recipients.length,
  };

  switch (mode) {
    case 'downloadAll':
      return eventData;

    case 'downloadFile':
      return {
        file_index: fileIndex,
        ...eventData,
      };
  }
}

export function prepareTransferDownload(transferDetails, password) {
  return (dispatch, getState) => {
    const { user } = getState();

    const items = (transferDetails.items || []).map(
      ({ file, item_type, ...item }) => ({
        ...item,
        file: file || new TransferItem(item),
        apiType: item_type,
      })
    );
    const normalizedTransfer = normalizeTransfer({ ...transferDetails, items });

    const message =
      'description' in normalizedTransfer
        ? normalizedTransfer.description
        : normalizedTransfer.message || '';

    const state =
      (normalizedTransfer.state === TRANSFER_STATES.PROCESSING ||
        normalizedTransfer.state === TRANSFER_STATES.DOWNLOADABLE) &&
      normalizedTransfer.password_protected === true &&
      !password
        ? TRANSFER_STATES.PASSWORD_PROTECTED
        : normalizedTransfer.state || TRANSFER_STATES.INIT;

    dispatch({
      type: TransferActionTypes.TRANSFER_INIT,
      id: normalizedTransfer.id,
      items: normalizedTransfer.items,
      itemsTree: normalizedTransfer.itemsTree,
      defaultProExpiry: user.rules.defaultProExpiry,
      defaultFreeExpiry: user.rules.defaultFreeExpiry,
      message,
      password:
        normalizedTransfer.password_protected && password ? password : null,
      shortURL: normalizedTransfer.shortened_url || null,
      title: normalizedTransfer.title,
      state,
      ...(normalizedTransfer.security_hash
        ? { secret: normalizedTransfer.security_hash }
        : {}),
      ...(normalizedTransfer.recipient_id
        ? { recipientId: normalizedTransfer.recipient_id }
        : {}),
      ...(normalizedTransfer.account_id
        ? { accountId: normalizedTransfer.account_id }
        : {}),
      ...('expiryInSeconds' in normalizedTransfer
        ? { expiryInSeconds: normalizedTransfer.expiryInSeconds }
        : {}),
    });
  };
}

export function fetchTransferDownloadDetails({
  id,
  secret,
  recipientId = false,
  password = false,
}) {
  return async (dispatch) => {
    dispatch({ type: TransferActionTypes.DOWNLOAD_PENDING });

    try {
      const transfer = await fetchTransfer(id, secret, recipientId, password);

      if (!transfer) {
        return dispatch({
          type: TransferActionTypes.TRANSFER_DOWNLOAD_EXPIRED,
        });
      }

      dispatch({ type: TransferActionTypes.DOWNLOAD_SUCCESS });
      if (transfer.state !== TRANSFER_STATES.PROCESSING) {
        dispatch(prepareTransferDownload(transfer, password));
      }
    } catch {
      dispatch({ type: TransferActionTypes.DOWNLOAD_ERROR });
    }
  };
}
